import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, type ReactNode } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { Spinner } from '@/components/ui/Spinner';
import { Logo } from '@/components/branding/Logo';

type AuthGuardProps = {
  children: ReactNode;
};

/**
 * Gates the admin shell on a valid Medusa session. The Medusa session cookie
 * is the source of truth — if `sdk.admin.user.me()` returns no user, we
 * redirect to /login while preserving the originally requested URL.
 *
 * `initialize()` is called here (not in `AdminLayout`) because AuthGuard is
 * the only component that always renders on protected routes — without this,
 * `AdminLayout` would never mount, so the auth check would never fire.
 */
export const AuthGuard = ({ children }: AuthGuardProps) => {
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const initialize = useAuthStore((s) => s.initialize);
  const location = useLocation();

  useEffect(() => {
    if (!isInitialized) void initialize();
  }, [isInitialized, initialize]);

  if (!isInitialized || isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-surface-base">
        <Logo size={40} />
        <Spinner />
        <p className="text-sm text-fg-subtle">Vérification de la session…</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
};
